<template>
  <div :id="`${selected.label}-dropdown`" class="relative border-dashed" ref="dropdown">
    <div class="relative w-full">
      <button
        data-test="availableSuperFundsDropdown"
        type="button"
        class="flex bg-white justify-between items-center border-grey-3 enabled:hover:otivo-outline enabled:focus:otivo-outline enabled:active:otivo-outline py-[10px] px-[15px] border-[1px] rounded-[5px] text-button-3 w-full"
        :class="{
          'rounded-b-none otivo-outline': open,
          'bg-grey-4 border-grey-field cursor-not-allowed': disabled,
        }"
        @click="toggleDropdown">
        <span class="truncate">{{ selected.label }}</span>
        <Chevron fill="#696969" :class="{ 'rotate-180': open }" />
      </button>
    </div>

    <!-- Dropdown list -->
    <div
      v-if="open"
      class="absolute bg-white w-full border border-t-0 border-grey-2 otivo-outline rounded-b drop-shadow overflow-hidden z-10">
      <!-- Loop through groups (for example: Single and Partner) -->
      <template v-for="group in groupedMapSupers" :key="group.title">
        <!-- Group divider: a line, the group title, and another line -->
        <div class="flex items-center px-4 pt-6">
          <div class="w-[7%] border-t border-grey-2"></div>
          <span class="mx-2 text-grey-2 text-left">{{ group.title }}</span>
          <div class="flex-1 border-t border-grey-2"></div>
        </div>
        <!-- Options: list only complete super funds -->
        <div
          v-for="(item, idx) in group.options"
          :key="`${item.value.id}-${idx}`"
          @click="select(item)"
          tabindex="0"
          :data-test="superFundName(item.value.fund_name) + '-option'"
          class="cursor-pointer mx-2 my-2 rounded text-14px p-4 font-medium hover:bg-blue-5">
          {{ item.value.fund_name }}
        </div>
        <div
          v-if="(isCFSWhitelabel() && isAdmin()) || !isCFSWhitelabel()"
          @click="addSuperFund(group.owner)"
          tabindex="0"
          data-test="addSuperFundButton"
          class="cursor-pointer mx-2 my-2 rounded text-14px p-4 font-medium hover:bg-blue-5">
          <span class="flex items-center">
            <AdditionIcon fill="otivo-blue" class="mr-2" />
            Add another super fund
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, defineProps } from 'vue'
import { onClickOutside } from '@vueuse/core'
import Chevron from '@/components/SVGS/Chevron.vue'
import AdditionIcon from '@/components/icons/AdditionIcon.vue'
import { InputItem } from '@/types/InputItem.ts'
import SuperObject, { createNewSuperObject } from '@/types/api/super/SuperObject'
import { useSuperStore } from '@/store/pinia/SuperStore'
import { useUserStore } from '@/store/pinia/UserStore'
import { useCheckUser } from '@/composables/users/checkUser.ts'
import { isCFSWhitelabel } from '@/composables/isCFSWhitelabel.ts'
import { superInvestmentRecommendationHandlers } from '@/components/CFS/IntrafundInvestment/Utils/superInvestmentRecommendationHandlers.ts'
import { useIntrafundAdviceStore } from '@/store/pinia/IntrafundAdviceStore'
const { isAdmin } = useCheckUser()

type DropdownProps = {
  disabled?: boolean
  loading?: boolean
  existingItem?: InputItem<any>
}
const props = withDefaults(defineProps<DropdownProps>(), {
  disabled: false,
  loading: false,
})

const superStore = useSuperStore()
const userStore = useUserStore()
const intrafundAdviceStore = useIntrafundAdviceStore()
const supersArray = computed(() => superStore.getSupersArray)
const user = computed(() => userStore.getUser)

// Compute display name based on active super owner.
const superOwnerName = computed(() => {
  return superStore.getActiveSuperObject?.owner === 'partner'
    ? user.value.partner?.first_name || 'Partner'
    : user.value.first_name || 'User'
})

// Ref to control whether the dropdown is open.
const open = ref(false)

// Our selected super record (label and value).
const selected = ref({
  label:
    `${superOwnerName.value} - ${superStore.getActiveSuperObject?.fund_name}` ||
    'Select super fund',
  value: null,
})

// Close the dropdown when clicking outside.
const dropdown = ref<HTMLElement | null>(null)
onClickOutside(dropdown, () => {
  open.value = false
})

// Grouping: only complete super funds are shown in the list.
// Groups are defined for both single (primary) user and, when appropriate, partner.
const groupedMapSupers = computed(() => {
  const primaryUserName = user.value.first_name || 'User'
  const partnerName = user.value.partner?.first_name || null

  const groups: { title: string; options: InputItem<SuperObject>[]; owner: string }[] = []

  // Primary (single) user group: note your owner filter is now "single"
  const userOptions: InputItem<SuperObject>[] = supersArray.value
    .filter(
      (superAccount: SuperObject) =>
        superAccount.owner === 'single' && !!superAccount.chant_west_product_id,
    )
    .map((superAccount: SuperObject) => ({
      label: superAccount.fund_name,
      value: superAccount,
    }))
  groups.push({ title: primaryUserName, options: userOptions, owner: 'single' })

  // Only add partner group if we're NOT in CFS Whitelabel mode.
  if (partnerName && !isCFSWhitelabel()) {
    const partnerOptions: InputItem<SuperObject>[] = supersArray.value
      .filter(
        (superAccount: SuperObject) =>
          superAccount.owner === 'partner' && !!superAccount.chant_west_product_id,
      )
      .map((superAccount: SuperObject) => ({
        label: superAccount.fund_name,
        value: superAccount,
      }))
    groups.push({ title: partnerName, options: partnerOptions, owner: 'partner' })
  }
  return groups
})

const { isUser } = useCheckUser()
const { fetchSuperInvestmentAdvice } = superInvestmentRecommendationHandlers(
  userStore,
  superStore,
  isUser,
)

function toggleDropdown() {
  open.value = !open.value
}

// When the user selects an existing super fund.
const select = async (item: InputItem<any>) => {
  if (item.value.id === superStore.getActiveSuperObject?.id) return
  intrafundAdviceStore.resetSuperInvestmentAdvice()
  superStore.resetSuperProductInvestmentOptions()

  superStore.setActiveSuperObject(item.value as SuperObject)
  open.value = false
  await fetchSuperInvestmentAdvice()
  if (item.value.chant_west_product_id) {
    await superStore.getInvestmentOptionsForActiveSuper()
  }
  // Update selected value.
  selected.value = {
    label: `${superOwnerName.value} - ${item.value.fund_name}`,
    value: item.value,
  }
}

// When "Add another super fund" button is clicked.
// It checks for a partially complete super fund and either loads or creates it.
async function addSuperFund(owner: string) {
  // If in CFS Whitelabel mode, disallow partner modifications.
  if (isCFSWhitelabel() && owner === 'partner') {
    return
  }
  let incompleteSuper = isCFSWhitelabel()
    ? undefined
    : supersArray.value.find(
        (superAccount: SuperObject) =>
          superAccount.owner === owner && !superAccount.chant_west_product_id,
      )
  open.value = false
  if (!incompleteSuper) {
    incompleteSuper = createNewSuperObject()
    incompleteSuper.owner = owner
    incompleteSuper.fund_name = ''
  }
  selected.value = {
    label: 'Add another super fund',
    value: incompleteSuper,
  }
  superStore.resetSuperProductInvestmentOptions()
  superStore.setActiveSuperObject(incompleteSuper)
}

function superFundName(str: string): string {
  return str.replace(/\s+/g, '-')
}

// Watch for changes to props.existingItem.
watch(
  () => props.existingItem,
  (newVal) => {
    if (newVal) {
      selected.value = newVal
    }
  },
)
</script>
